import { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/components/utils/utils';

export interface MultiSelectOption {
    value: string;
    label: string;
}

interface MultiSelectProps {
    options: MultiSelectOption[];
    value: string[];
    onChange: (value: string[]) => void;
    /** Testo mostrato nel trigger quando non c'è nulla di selezionato. */
    placeholder: string;
    searchPlaceholder: string;
    emptyLabel: string;
    /** Etichetta dell'azione "svuota" in fondo alla tendina. */
    clearLabel: string;
    /** aria-label della X su ogni chip selezionato. */
    removeLabel: string;
    /**
     * Trigger ridotto a etichetta + chevron, senza i chip dei selezionati: serve quando
     * la selezione è già mostrata fuori dal componente (vedi `TagFilterBar`) e ripeterla
     * nel trigger sarebbe solo rumore.
     */
    compact?: boolean;
    className?: string;
}

/**
 * Tendina multi-selezione: il trigger mostra i valori scelti come chip in fila, ognuno
 * con la sua X per toglierlo senza riaprire la lista; la tendina resta aperta a ogni
 * scelta, perché selezionarne più d'uno è il caso normale (a differenza di
 * `FacetCombobox`, che è a singola scelta e si chiude al click).
 *
 * Il trigger è un `div` e non un `<button>`: le X dei chip sono elementi interattivi a
 * sé, e annidare un pulsante dentro un pulsante è HTML non valido (oltre a mandare il
 * click al trigger, riaprendo la tendina che si stava svuotando). Per lo stesso motivo
 * le X sono `span role="button"`, con stopPropagation sul click.
 */
export const MultiSelect = ({
    options,
    value,
    onChange,
    placeholder,
    searchPlaceholder,
    emptyLabel,
    clearLabel,
    removeLabel,
    compact = false,
    className,
}: MultiSelectProps) => {
    const [open, setOpen] = useState(false);

    const toggle = (optionValue: string) => {
        onChange(
            value.includes(optionValue)
                ? value.filter((existing) => existing !== optionValue)
                : [...value, optionValue]
        );
    };

    const remove = (optionValue: string) => {
        onChange(value.filter((existing) => existing !== optionValue));
    };

    // I chip riflettono l'ordine di selezione, non quello delle opzioni: un valore che
    // non è (più) tra le opzioni resta comunque mostrato e rimovibile.
    const selectedOptions = value.map(
        (selectedValue) =>
            options.find((option) => option.value === selectedValue) ?? {
                value: selectedValue,
                label: selectedValue,
            }
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                {compact ? (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        role="combobox"
                        aria-expanded={open}
                        className={cn('justify-between gap-1 font-normal', className)}
                    >
                        <span className="truncate">{placeholder}</span>
                        <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
                    </Button>
                ) : (
                <div
                    role="combobox"
                    aria-expanded={open}
                    tabIndex={0}
                    className={cn(
                        'flex min-h-9 w-full cursor-pointer flex-wrap items-center gap-1 rounded-md border border-input bg-background px-3 py-1 text-sm',
                        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                        className
                    )}
                >
                    {selectedOptions.length === 0 ? (
                        <span className="text-gray-500">{placeholder}</span>
                    ) : (
                        selectedOptions.map((option) => (
                            <span
                                key={option.value}
                                className="flex items-center gap-1 rounded-full bg-gray-900 px-2.5 py-0.5 text-xs text-white"
                            >
                                {option.label}
                                <span
                                    role="button"
                                    tabIndex={0}
                                    aria-label={removeLabel}
                                    title={removeLabel}
                                    className="cursor-pointer hover:text-gray-300"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        remove(option.value);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter' && e.key !== ' ') return;
                                        e.preventDefault();
                                        e.stopPropagation();
                                        remove(option.value);
                                    }}
                                >
                                    <X className="h-3 w-3" />
                                </span>
                            </span>
                        ))
                    )}
                    <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-gray-500" />
                </div>
                )}
            </PopoverTrigger>

            <PopoverContent className="w-64 p-0" align="start">
                <Command>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList>
                        <CommandEmpty>{emptyLabel}</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => (
                                <CommandItem
                                    key={option.value}
                                    value={option.label}
                                    onSelect={() => toggle(option.value)}
                                >
                                    <Check
                                        className={cn(
                                            'mr-2 h-4 w-4 shrink-0',
                                            value.includes(option.value)
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        )}
                                    />
                                    <span className="min-w-0 truncate">{option.label}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>

                {value.length > 0 && (
                    <div className="border-t border-gray-100 p-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-full justify-start px-2 text-xs text-gray-500 hover:text-gray-700"
                            onClick={() => onChange([])}
                        >
                            <X className="mr-1 h-3.5 w-3.5" />
                            {clearLabel}
                        </Button>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};
